import { router, useLocalSearchParams, Redirect } from 'expo-router';
import { Check, Copy, Share2 } from 'lucide-react-native';
import * as Clipboard from 'expo-clipboard';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { Image, Linking, Platform, StyleSheet, Text as RNText, View } from 'react-native';

import { BackChevron } from '@/components/BackChevron';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { DealRow } from '@/components/DealRow';
import { ErrorState } from '@/components/ErrorState';
import { Heading } from '@/components/Heading';
import { Overline } from '@/components/Overline';
import { Screen } from '@/components/Screen';
import { Text } from '@/components/Text';
import { APP_STORE_URL } from '@/constants/gifting';
import { colors, fonts, fontSizes, radii, spacing } from '@/constants/theme';
import { campaignCopy } from '@/lib/campaignCopy';
import { campaignShareUrl, shareCampaignLink } from '@/lib/campaignShare';
import { isAndroidWeb } from '@/lib/isIosWeb';
import { authStore } from '@/stores/auth.store';
import { dealsStore } from '@/stores/deals.store';

/**
 * Gift secured. Two modes (prototype's viewOnly split):
 *   fresh (?fresh=1, set by confirm) → celebration + "Pay it forward" share
 *   returning (home card "View gift") → quiet "Your gift" recap, NO repeat
 *   share ask (the ask already happened; Refer tab still has sharing)
 *
 * Share link: /campus-drop?ref=<ref_id> (see `constants/giftingOg.ts` for
 * why the branded alias and not the canonical path). `?ref` attributes the
 * referrer at signup via Users::Creator's ref_id path; per-link click
 * analytics are deliberately not wired for the campaign (tracked `?r=`
 * links are deal-coupled — accepted v1 limitation from the plan review).
 */
const GiftSecuredScreen = observer(() => {
  const { fresh } = useLocalSearchParams<{ fresh?: string }>();
  const viewOnly = fresh !== '1';
  const claim = authStore.user?.gift_claim;
  const refId = authStore.user?.ref_id;
  const [copied, setCopied] = useState(false);
  const [refreshFailed, setRefreshFailed] = useState(false);

  useEffect(() => {
    void dealsStore.ensureLoaded();
  }, []);

  // Reached without a claim on the local payload (already_claimed race, or a
  // stale session deep-linking here): fetch /me so the claim appears instead
  // of a permanent loading state. A failed refresh surfaces a retry, and a
  // successful refresh that STILL has no claim means this user doesn't
  // belong here — route them back to the campaign entry instead of leaving
  // "Loading…" forever (review findings).
  const hydrated = authStore.hydrated;
  useEffect(() => {
    // Wait for hydration: pre-hydration `claim` is null for EVERYONE, and
    // refreshMe() no-ops without a session token — the .then would then
    // bounce a genuinely-claimed user to the campaign entry (review finding).
    if (!hydrated || claim) return;
    let cancelled = false;
    authStore
      .refreshMe()
      .then(() => {
        if (!cancelled && !authStore.user?.gift_claim) router.replace('/season-of-gifting');
      })
      .catch(() => {
        if (!cancelled) setRefreshFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [claim, hydrated]);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1800);
    return () => clearTimeout(timer);
  }, [copied]);

  // Viewing your claim requires a session (review finding). After all hooks.
  if (authStore.hydrated && !authStore.user) {
    return <Redirect href="/season-of-gifting" />;
  }

  if (!claim) {
    return (
      <Screen width="column">
        <BackChevron onPress={() => router.replace('/season-of-gifting')} />
        {refreshFailed ? (
          <ErrorState
            title="Couldn't load your gift"
            body="Check your connection and try again."
            onRetry={() => {
              setRefreshFailed(false);
              authStore
                .refreshMe()
                .then(() => {
                  if (!authStore.user?.gift_claim) router.replace('/season-of-gifting');
                })
                .catch(() => setRefreshFailed(true));
            }}
          />
        ) : (
          <Text color="textMuted" align="center" style={styles.loadingText}>
            Loading your gift…
          </Text>
        )}
      </Screen>
    );
  }

  const shareUrl = campaignShareUrl(refId);
  const shareMessage = campaignCopy(authStore.user?.gifting_copy).share_text;

  async function copyLink() {
    try {
      await Clipboard.setStringAsync(shareUrl);
      setCopied(true);
    } catch {
      // Clipboard unavailable — don't claim "Copied" when nothing happened.
    }
  }

  async function shareLink() {
    try {
      await shareCampaignLink(shareMessage, shareUrl);
    } catch {
      await copyLink();
    }
  }

  const earnDeals = dealsStore.deals.filter((deal) => !deal.full).slice(0, 3);

  return (
    <Screen width="column">
      <BackChevron onPress={() => router.replace('/(tabs)')} />
      <View style={styles.body}>
        <View style={styles.badge}>
          <Check color={colors.deepGreen} size={28} strokeWidth={2.5} />
        </View>
        <Heading size="4xl" style={styles.title}>
          {viewOnly ? 'Your gift' : 'Your free gift is claimed'}
        </Heading>
        <Text color="textMuted" align="center" style={styles.subcopy}>
          You claimed {claim.product.name}. Let&apos;s go! We&apos;ll keep you posted when
          there&apos;s an update on shipping.
        </Text>

        <Card style={styles.claimCard}>
          <View style={styles.claimRow}>
            <View style={styles.claimImageWrap}>
              {claim.product.image_url ? (
                <Image
                  source={{ uri: claim.product.image_url }}
                  style={styles.claimImage}
                  resizeMode="cover"
                />
              ) : (
                <View style={styles.claimImageFallback}>
                  <RNText style={styles.claimImageFallbackText}>
                    {claim.product.name.slice(0, 1)}
                  </RNText>
                </View>
              )}
            </View>
            <View style={styles.claimMeta}>
              <Text variant="bodySemiBold">{claim.product.name}</Text>
              <View style={styles.securedRow}>
                <Check color={colors.accentText} size={16} />
                <Text variant="caption" color="accentText">
                  Gift claimed
                </Text>
              </View>
            </View>
          </View>
        </Card>

        {Platform.OS === 'web' &&
          (isAndroidWeb() ? (
            // No Android app exists — pitching the Apple App Store here would
            // be a dead end. The web app IS their home; point them at it
            // (Jeff, 8/7).
            <Card style={styles.getAppCard}>
              <Text variant="bodySemiBold">You&apos;re all set</Text>
              <Text variant="caption" color="textMuted" style={styles.getAppCopy}>
                Everything lives right here on the web — track your gift and earn cash from
                deals like SoFi.
              </Text>
              <Button
                label="Go to home"
                size="sm"
                onPress={() => router.replace('/(tabs)')}
                style={styles.getAppCta}
              />
            </Card>
          ) : (
            // The ONLY app pitch in the campaign — deliberately here and
            // nowhere earlier: an install detour mid-signup loses people, and
            // this lands after the gift is already locked in. Sells the paid
            // deals, not shipment tracking (no carrier feed exists).
            <Card style={styles.getAppCard}>
              <Text variant="bodySemiBold">Keep the good stuff coming</Text>
              <Text variant="caption" color="textMuted" style={styles.getAppCopy}>
                Your gift&apos;s locked in. The app is where you&apos;ll see it — and where brands
                pay you cash for deals like SoFi.
              </Text>
              <Button
                label="Get the app"
                size="sm"
                onPress={() => void Linking.openURL(APP_STORE_URL)}
                style={styles.getAppCta}
              />
            </Card>
          ))}

        {/* Share card renders in BOTH modes (2026-08-07, was fresh-claim
            only): the referral loop is the campaign's growth engine, and
            "View my gift" was the one place a claimer could never find
            their link again. */}
        {(
          <Card variant="dark" style={styles.shareCard}>
            <Heading size="2xl" tone="inverse">
              Don&apos;t gatekeep the free stuff
            </Heading>
            <Text color="textOnDarkMuted" style={styles.shareCopy}>
              Send your link to your teammates so they can meet a new brand and claim a free
              product too.{'\n\n'}Good friends don&apos;t let good friends miss out on free
              stuff :)
            </Text>
            <View style={styles.shareUrlPill}>
              <RNText style={styles.shareUrlText} numberOfLines={1}>
                {shareUrl.replace(/^https?:\/\//, '')}
              </RNText>
            </View>
            <View style={styles.shareButtons}>
              <Button
                label={copied ? 'Copied' : 'Copy my link'}
                variant="outlineOnDark"
                size="sm"
                leadingIcon={copied ? Check : Copy}
                onPress={() => void copyLink()}
                style={styles.shareButton}
              />
              <Button
                label="Share link"
                size="sm"
                leadingIcon={Share2}
                onPress={() => void shareLink()}
                style={styles.shareButton}
              />
            </View>
          </Card>
        )}

        {earnDeals.length > 0 && (
          <View style={styles.earnSection}>
            <Overline style={styles.earnLabel}>More ways to earn</Overline>
            <View style={styles.earnList}>
              {earnDeals.map((deal) => (
                <DealRow
                  key={deal.slug}
                  deal={deal}
                  action={undefined}
                  onPress={() => router.push({ pathname: '/deals/[slug]', params: { slug: deal.slug } })}
                />
              ))}
            </View>
          </View>
        )}

        <Button
          label="Explore more Rewards"
          variant="outline"
          onPress={() => router.replace('/(tabs)')}
          style={styles.exploreCta}
        />
      </View>
    </Screen>
  );
});

export default GiftSecuredScreen;

const styles = StyleSheet.create({
  body: {
    paddingTop: spacing.sm,
    alignItems: 'stretch',
  },
  badge: {
    alignSelf: 'center',
    width: 56,
    height: 56,
    borderRadius: radii.full,
    backgroundColor: colors.brightGreenSoft,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing.base,
  },
  title: {
    textAlign: 'center',
  },
  subcopy: {
    marginTop: spacing.md,
  },
  claimCard: {
    marginTop: spacing.lg,
  },
  claimRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.base,
  },
  claimImageWrap: {
    width: 64,
    height: 64,
    borderRadius: radii.base,
    overflow: 'hidden',
    backgroundColor: colors.background,
  },
  claimImage: {
    width: '100%',
    height: '100%',
  },
  claimImageFallback: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.brightGreenSoft,
  },
  claimImageFallbackText: {
    fontFamily: fonts.display,
    fontSize: fontSizes['2xl'],
    color: colors.deepGreen,
  },
  claimMeta: {
    flex: 1,
    gap: spacing.xs,
  },
  securedRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  getAppCard: {
    marginTop: spacing.lg,
  },
  getAppCopy: {
    marginTop: spacing.xs,
  },
  getAppCta: {
    marginTop: spacing.md,
    alignSelf: 'flex-start',
  },
  shareCard: {
    marginTop: spacing.lg,
  },
  shareCopy: {
    marginTop: spacing.sm,
  },
  shareUrlPill: {
    marginTop: spacing.base,
    backgroundColor: colors.glassFrostFill,
    borderWidth: 1,
    borderColor: colors.glassFrostBorder,
    borderRadius: radii.base,
    paddingHorizontal: spacing.base,
    paddingVertical: spacing.md,
  },
  shareUrlText: {
    fontFamily: fonts.bodyMedium,
    fontSize: fontSizes.sm,
    color: colors.frost,
  },
  shareButtons: {
    flexDirection: 'row',
    gap: spacing.md,
    marginTop: spacing.md,
  },
  shareButton: {
    flex: 1,
  },
  earnSection: {
    marginTop: spacing.xl,
  },
  earnLabel: {
    marginBottom: spacing.md,
  },
  earnList: {
    gap: spacing.md,
  },
  exploreCta: {
    marginTop: spacing.xl,
  },
  loadingText: {
    paddingVertical: spacing.xl,
  },
});
